import { Button, Modal, notification, Space } from 'antd'
import axios from 'axios'
import React, { useState } from 'react'

import { CsvContent } from '../../../components/LoadFile/CsvContent'
import { LoadFile } from '../../../components/LoadFile/LoadFile'
import { LoadFileError } from '../../../components/LoadFileError/LoadFileError'
import { momentDateHandler } from '../../../date/moment-date-handler'
import { modalWidth, responsiveModalWidth } from '../../../util/guideline'
import { UpdateIsin } from '../models'

const { transformDateToIsoFormat } = momentDateHandler

const buildIsinsPayload = (isins: any[]) =>
  isins.map(({ Row, frnSwapRate, ...isin }) => ({
    ...isin,
    eodDate: transformDateToIsoFormat(isin.eodDate),
    couponDate: transformDateToIsoFormat(isin.couponDate),
    swapRate: frnSwapRate || undefined,
    yieldOrTradeMargin: isin.yieldOrTradeMargin || undefined,
  }))

const csvColumns = ['isin', 'eodDate', 'cleanPrice', 'grossPrice', 'couponDate', 'frnSwapRate', 'yieldOrTradeMargin']

export const UploadIsinPrices = () => {
  const [showLoadedFileModal, setShowLoadedFileModal] = useState(false)
  const [uploadErrors, setUploadErrors] = useState(null)
  const [isinsToUpdate, setIsinsToUpdate] = useState<UpdateIsin[]>([])
  const [isUpdating, setIsUpdating] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)

  const updateIsinPrices = async () => {
    setIsUpdating(true)

    try {
      await axios.post('/isin-prices', buildIsinsPayload(isinsToUpdate))
      notification.success({
        message: 'Success',
        description: 'Success updating ISINs',
      })
    } catch (errors) {
      setUploadErrors(errors)
    }

    setIsUpdating(false)
  }

  const onLoad = (isins: UpdateIsin[]) => {
    setIsinsToUpdate(isins)
    setShowLoadedFileModal(true)
    setShowUploadModal(false)
  }

  const onError = () => {
    setIsinsToUpdate([])
  }

  return (
    <>
      <Button type="primary" onClick={() => setShowUploadModal(true)}>
        Upload daily ISIN prices (CSV)
      </Button>

      <Modal
        title="Upload File"
        centered
        open={showUploadModal}
        width={responsiveModalWidth}
        onCancel={() => {
          setShowUploadModal(false)
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowUploadModal(false)
            }}
          >
            Cancel
          </Button>,
        ]}
      >
        <Space direction="vertical" className="full-width">
          <LoadFile onLoad={onLoad} onError={onError} headers={csvColumns} />
          <a href="upload-isins-sample.csv">Download Sample CSV File</a>
        </Space>
      </Modal>
      <Modal
        title="CSV Content"
        centered
        open={showLoadedFileModal}
        width={responsiveModalWidth}
        onCancel={() => {
          setShowLoadedFileModal(false)
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowLoadedFileModal(false)
            }}
          >
            Cancel
          </Button>,
          <Button
            key="retry"
            onClick={() => {
              setIsinsToUpdate([])
              setShowUploadModal(true)
              setShowLoadedFileModal(false)
            }}
          >
            Retry
          </Button>,
          <Button
            key="update"
            type="primary"
            loading={isUpdating}
            onClick={async () => {
              await updateIsinPrices()
              setShowLoadedFileModal(false)
            }}
          >
            Update ISINs
          </Button>,
        ]}
      >
        <CsvContent data={isinsToUpdate} filters={['isin']} rowKey="isin" />
      </Modal>
      <Modal
        title="Errors in CSV File"
        centered
        open={!!uploadErrors}
        width={modalWidth}
        onCancel={() => {
          setUploadErrors(null)
        }}
        onOk={() => {
          setUploadErrors(null)
          setShowUploadModal(true)
        }}
        okText="Retry"
      >
        <LoadFileError payload={uploadErrors} />
      </Modal>
    </>
  )
}
